import { useState } from 'react';
import { Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { admin } from '@repletoryreach/sdk-api';
import { useUpdateAiModel } from '../../../../data/useAiModels';
import { formatInteger, formatMinor } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { canTest, needsBucket, type ModelPrice } from '../aiModels';

/**
 * Switching a model on or off, always behind a confirmation (UI-CONVENTIONS §5): on, businesses can choose it and
 * are charged for it; off, every business using it is refused with "model unknown". A model that runs outside
 * South Africa says so in the confirmation, because the business's prompts and pictures go with it.
 */
export function useModelSwitch({ priceOf, tests }: {
  priceOf: (m: admin.AdminAiModel) => ModelPrice | null;
  tests: Record<string, admin.AiModelTest>;
}) {
  const t = useT();
  const update = useUpdateAiModel();
  const [busy, setBusy] = useState<string | null>(null);

  function save(m: admin.AdminAiModel, on: boolean) {
    setBusy(m.code);
    update.mutate({ code: m.code, change: { enabled: on } }, {
      onSuccess: () => notifySuccess(t(on ? 'billing.aiModels.toast.on' : 'billing.aiModels.toast.off', { name: m.name }), t('billing.aiModels.title')),
      onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.aiModels.toast.failed', { name: m.name })),
      onSettled: () => setBusy(null)
    });
  }

  function confirmOff(m: admin.AdminAiModel) {
    openConfirmModal({
      title: t('billing.aiModels.off.title', { name: m.name }),
      children: (
        <Stack gap="sm">
          <Text role="bodySmall">{t('billing.aiModels.off.body', { name: m.name, code: m.code })}</Text>
          {m.calls_last30_days > 0 && (
            <Text role="bodySmall">
              {t('billing.aiModels.off.usage', { count: formatInteger(m.calls_last30_days), charged: formatMinor(m.charged_last30_days_minor, 'ZAR') })}
            </Text>
          )}
        </Stack>
      ),
      labels: { confirm: t('billing.aiModels.off.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => save(m, false)
    });
  }

  function confirmOn(m: admin.AdminAiModel) {
    const price = priceOf(m);
    const test = tests[m.code];
    const testLine = !canTest(m)
      ? { tone: 'muted', text: t('billing.aiModels.on.cannotTest') }
      : !test
        ? { tone: 'warning', text: t('billing.aiModels.on.untested') }
        : !test.ok
          ? { tone: 'error', text: t('billing.aiModels.on.testFailed', { error: test.error ?? t('common.unknownError') }) }
          : test.stub
            ? { tone: 'warning', text: t('billing.aiModels.on.testedStub') }
            : { tone: 'success', text: t('billing.aiModels.on.tested', { ms: formatInteger(test.milliseconds) }) };
    const colour: Record<string, string> = {
      muted: 'var(--rr-color-text-muted)',
      warning: 'var(--rr-color-warning)',
      error: 'var(--rr-color-error)',
      success: 'var(--rr-color-success)'
    };
    openConfirmModal({
      title: t('billing.aiModels.on.title', { name: m.name }),
      children: (
        <Stack gap="sm">
          <Text role="bodySmall">{t('billing.aiModels.on.body', { name: m.name, code: m.code })}</Text>
          {m.outside_south_africa && (
            <Alert color="warning" title={t('billing.aiModels.on.outsideTitle')}>
              {m.region?.trim()
                ? t('billing.aiModels.on.outsideBody', { name: m.name, region: m.region })
                : t('billing.aiModels.on.outsideBodyNoRegion', { name: m.name })}
            </Alert>
          )}
          {needsBucket(m) && <Text role="bodySmall" c="var(--rr-color-error)">{t('billing.aiModels.on.noBucket')}</Text>}
          {price && (price.unpriced || price.missing.length > 0) && (
            <Text role="bodySmall" c="var(--rr-color-error)">
              {price.unpriced
                ? t('billing.aiModels.on.unpriced')
                : t('billing.aiModels.on.noPrice', { skus: price.missing.join(', ') })}
            </Text>
          )}
          <Text role="bodySmall" c={colour[testLine.tone]}>{testLine.text}</Text>
        </Stack>
      ),
      labels: {
        confirm: m.outside_south_africa ? t('billing.aiModels.on.confirmOutside') : t('billing.aiModels.on.confirm'),
        cancel: t('action.cancel')
      },
      confirmProps: { color: m.outside_south_africa || (test && !test.ok) ? 'red' : 'orange' },
      onConfirm: () => save(m, true)
    });
  }

  return {
    /** The code of the model being saved, so its switch waits. */
    busy,
    toggle: (m: admin.AdminAiModel, on: boolean) => (on ? confirmOn(m) : confirmOff(m))
  };
}
